import { useEffect, useRef } from 'react';

import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';
import { AddChildIcon, EditIcon, TrashIcon } from '@shared/ui/icons';

import { childrenOf, hasChildren } from '../_shared/tree';
import type { CategoryNode } from '../_shared/types';
import { copy } from './copy';

/**
 * The genre tree — a native `<details>` / `<summary>` disclosure per branch,
 * which is what the design system names for this component by name: it works
 * without JavaScript, it announces its own state for free, and the disclosure
 * triangle is the browser's and mirrors with the document without a line written
 * for it.
 *
 * **Nothing interactive goes inside `<summary>`**, and `display` stays at its
 * default: `flex` or `grid` on a `<summary>` drops the marker in several
 * engines, so the 44 px target comes from block padding instead.
 *
 * **The row menu is a sibling of `<details>`, never a child.** A child is hidden
 * with the rest of the branch the moment the branch collapses, and a folded
 * category would lose its edit and delete actions entirely. The row is a flex
 * line aligned on `flex-start`, so the handle sits level with the summary rather
 * than with the middle of the subtree.
 *
 * **A childless root renders as a childless root.** No fabricated child is added
 * to make the shape uniform — that is the same fabrication the domain model
 * refuses when it declines a synthetic «Season 1» for a film, and for the same
 * reason: a reader could no longer tell a genre with nothing under it from a
 * branch nobody has expanded.
 *
 * **Branches open by default, and that is a console decision rather than a
 * transcription.** The mockup draws one branch open and three folded, which is a
 * static file demonstrating that the disclosure works; on a management surface
 * the vocabulary the operator came to edit should be on screen, and every branch
 * is still collapsible.
 */

export interface TermTreeProps {
  nodes: readonly CategoryNode[];
  onEdit: (node: CategoryNode) => void;
  onAddChild: (node: CategoryNode) => void;
  onDelete: (node: CategoryNode) => void;
}

export function TermTree({ nodes, onEdit, onAddChild, onDelete }: TermTreeProps) {
  const rootRef = useRef<HTMLUListElement>(null);

  /**
   * Every collapsed branch opens for the printer and closes again after.
   *
   * A stylesheet cannot do this: engines hide a folded `<details>` through an
   * internal mechanism no author rule reaches, and `::details-content` has not
   * landed in all three. A printed vocabulary missing its folded branches
   * misreports itself — and a tree left wide open after printing misreports the
   * operator's own view back at them, so the state is restored rather than
   * abandoned.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let reopened: HTMLDetailsElement[] = [];

    function onBeforePrint() {
      if (!root) return;
      reopened = Array.from(root.querySelectorAll('details')).filter((element) => !element.open);
      for (const element of reopened) element.open = true;
    }

    function onAfterPrint() {
      for (const element of reopened) element.open = false;
      reopened = [];
    }

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);

  return (
    <ul ref={rootRef} aria-label={copy.section.treeLabel} className="m-0 list-none p-0 text-sm">
      {nodes.map((node) => (
        <TermNode key={node.id} node={node} onEdit={onEdit} onAddChild={onAddChild} onDelete={onDelete} />
      ))}
    </ul>
  );
}

function TermNode({
  node,
  onEdit,
  onAddChild,
  onDelete,
}: { node: CategoryNode } & Omit<TermTreeProps, 'nodes'>) {
  const branch = hasChildren(node);

  const entries: RowMenuEntry[] = [
    {
      key: `${node.id}:edit`,
      label: copy.menu.edit,
      icon: <EditIcon className="flex-none" />,
      onSelect: () => onEdit(node),
    },
    {
      key: `${node.id}:add-child`,
      label: copy.menu.addChild,
      icon: <AddChildIcon className="flex-none" />,
      onSelect: () => onAddChild(node),
    },
    {
      key: `${node.id}:delete`,
      label: copy.menu.delete,
      icon: <TrashIcon className="flex-none" />,
      danger: true,
      onSelect: () => onDelete(node),
    },
  ];

  return (
    <li>
      <div className="flex items-start gap-2">
        {branch ? (
          <details open className="min-w-0 flex-1">
            <summary className="cursor-pointer rounded-xs px-1 py-3 hover:bg-hover-bg">
              <span className="font-medium text-ink">{node.name}</span>
            </summary>
            <ul className="m-0 list-none border-s border-border p-0 ps-4">
              {childrenOf(node).map((child) => (
                <TermNode
                  key={child.id}
                  node={child}
                  onEdit={onEdit}
                  onAddChild={onAddChild}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </details>
        ) : (
          /*
           * A leaf takes the summary's own padding so sibling names stand on one
           * ruler whether the sibling carries children or not — and it is not a
           * handle: the sitemap holds no route for a category node, so the name
           * is text rather than a link or a button.
           */
          <span className="min-w-0 flex-1 px-1 py-3 font-medium text-ink">{node.name}</span>
        )}

        <RowMenu label={copy.menu.label(node.name)} entries={entries} />
      </div>
    </li>
  );
}
